import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Flex } from '@chakra-ui/react';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { fetchPackagesHistory } from 'api/history';

import { CSVLink } from 'react-csv';

import { Filters } from 'components/primitives/Filters';
import { DataTable } from 'components/modules/DataTable';

import { History } from 'types/history';

import { exportCsvPackagesHistory } from 'api/packages';
import { useSites } from 'hooks/sites/useSites';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';
import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import { buildPackagesHistoryColumns } from './packagesHistoryColumns';

export const PackagesHistory: FC<{ compType?: string }> = ({
  compType = 'packg',
}) => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(1);
  const [sku, setSku] = useState<string[]>();
  const [venue, setVenue] = useState<string[]>();
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [type, setType] = useState<string>();
  const [urlRecord, setUrlRecord] = useState({});
  const queryUrl = useQueryParams();

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';
  const washHubName = selectedWashHub?.name || '';
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washHubId);

  const sites = useSites();
  const allSites = sites.data || [];

  const queryKey = [
    `fetch-packages-history-${washHubId}`,
    page,
    sku,
    venue,
    endDate,
    startDate,
  ];

  const queryFn = () =>
    fetchPackagesHistory({
      sku,
      venue,
      page,
      limit: 10,
      washHub: washHubId,
      endDate,
      startDate,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
    refetchOnMount: 'always' as any,
  };

  useEffect(() => {
    const urlParams = {
      washhubId: String(washHubId),
      compType,
      ...(type ? { type } : {}),
      ...(venue && venue.length ? { venue: venue.join('-') } : {}),
      ...(sku && sku.length ? { sku: sku.join('-') } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, venue, sku, type, endDate, startDate]);

  useEffect(() => {
    const { venue, sku, type, startDate, endDate } = queryUrl;
    venue && setVenue(venue.split('-'));
    sku && setSku(sku.split('-'));
    type && setType(type);
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  useEffect(() => () => setUrlRecord({}), []);

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  const csvRef = useRef<any>(null);

  const { mutate, data: csvData = '', isLoading: isExporting } = useMutation(
    exportCsvPackagesHistory,
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.link?.click?.();
        }, 500);
      },
    },
  );

  useWashHubListener(() => {
    setPage(1);

    setSku(undefined);
    setVenue(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
  });

  const packagesHistory = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);
    setType(type);
    if (type === 'sku') setSku(data);
    if (type === 'site') setVenue(data);
  };

  const onClearFilters = () => {
    setPage(1);

    setSku(undefined);
    setVenue(undefined);
  };

  const onUpdateClick = () => {
    onClearFilters();

    queryClient.invalidateQueries(`fetch-packages-history-${washHubId}`);
  };

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    setPage(1);

    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }
  };

  const onSearchClear = () => {
    setPage(1);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const columns = useMemo(() => buildPackagesHistoryColumns(allSites), [
    allSites,
  ]);

  const renderRefreshButton = () => {
    return (
      <Button isLoading={isLoading} ml={2} onClick={onUpdateClick}>
        Update Table
      </Button>
    );
  };

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isExporting}
          onClick={() => {
            mutate({
              sku,
              venue,
              washHub: washHubId,
              startDate,
              endDate,
            });
          }}>
          Export Packages history to CSV
        </Button>
        {!isExporting && Boolean(csvData) && (
          <CSVLink
            data={csvData}
            ref={csvRef}
            filename={`washhub-${washHubName}-packages-history.csv`}
            target="_blank"
          />
        )}
      </>
    );
  };

  if (!isUrlMapped) return null;

  return (
    <>
      <DateRangeInputExt
        startDate={startDate}
        endDate={endDate}
        onSearchClear={onSearchClear}
        onSearchPress={onSearchPress}
      />
      <Flex mb={5}>
        {renderExpBtn()}
        {renderRefreshButton()}
      </Flex>
      <Filters
        venue={type ? (type == 'sku' ? sku : venue) : []}
        isFullButton
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
      />
      <DataTable<History>
        minW="1800px"
        data={packagesHistory}
        columns={columns}
        isLoading={isLoading || isFetching}
        pagination={pagination}
      />
    </>
  );
};
